<template>
    <v-content>
        <v-toolbar class="content-toolbar">
            <font-awesome-icon :icon="['fal', 'file-image']" size="2x"/>
            <v-toolbar-title>Assets
                <template v-if="selectedMediaFilter && selectedMediaFilter.category !== this.allMediaType.category"> >
                    {{selectedMediaFilter.category}}
                </template>
            </v-toolbar-title>
            <div class="search-input-container">
                <font-awesome-icon :icon="['fal', 'search']" class="search-input"/>
                <v-text-field color="accent" placeholder="Type to search for entries" hide-details
                              v-model="searchFilterTerm"
                ></v-text-field>
            </div>


            <v-spacer></v-spacer>
            <div class="add-entry-dropdown-container">
                <v-btn color="info" :to="{ name: 'spaceDashboardAddMedia'}">
                    <span class="settings-icon-spacer capitalize-text">Add Asset</span>
                </v-btn>
                <v-menu offset-y>
                    <v-btn slot="activator" color="info">
                        <font-awesome-icon :icon="['fal', 'chevron-down']"/>
                    </v-btn>
                    <v-list>
                        <v-list-tile
                                :to="{ name: 'addMultipleMedia'}">
                            <v-list-tile-title>
                                <span class="capitalize-text">Multiple Assets</span>
                            </v-list-tile-title>
                        </v-list-tile>
                    </v-list>
                </v-menu>
            </div>
        </v-toolbar>
        <div class="content-container">
            <div class="dashboard-sidebar-container">
                <v-navigation-drawer permanent>
                    <v-subheader>Filters</v-subheader>
                    <template v-if="mediaTypes && selectedMediaFilter">
                        <v-list>
                            <template v-for="(mediaType, index ) in mediaTypes">
                                <v-divider></v-divider>
                                <v-list-tile v-on:click="selectMediaFilter(mediaType)"
                                             v-bind:class="{'selected-filter': mediaType.mimeType === selectedMediaFilter.mimeType}">
                                    <v-list-tile-title>{{mediaType.category}}</v-list-tile-title>
                                </v-list-tile>
                                <v-divider v-if="index === 0" class="all-filter-divider"></v-divider>
                            </template>
                        </v-list>
                        <v-divider></v-divider>
                        <v-container fluid>
                            <v-checkbox
                                    v-model="showArchived"
                                    hide-details
                            >
                                <div slot="label" class="filter-checkbox-label">
                                    Show archived entries
                                </div>
                            </v-checkbox>
                        </v-container>
                    </template>
                    <v-progress-linear v-else :indeterminate="true" color="secondary"></v-progress-linear>
                </v-navigation-drawer>
            </div>
            <v-container fill-height>
                <v-layout>
                    <v-flex>
                        <v-data-table :headers="headers"
                                      :items="mappedEntries"
                                      :pagination.sync="storePaginationSync"
                                      :loading="!entries"
                                      class="elevation-1 interactive-data-table hidden-rows-per-page-selection"
                        >
                            <v-progress-linear slot="progress" :indeterminate="true" color="accent"></v-progress-linear>
                            <template slot="items" slot-scope="props">
                                <tr v-on:click="navigateEdit(props.item)">
                                    <td>
                                        <div class="thumbnail-container">
                                            <img v-if="props.item.category === 'Image'" :src="props.item.url" alt=""
                                                 class="data-table-thumbnail">
                                            <font-awesome-icon v-else-if="props.item.category === 'Audio'"
                                                               :icon="['fal', 'file-audio']" size="2x"/>
                                            <font-awesome-icon v-else-if="props.item.category === 'PDF'"
                                                               :icon="['fal', 'file-pdf']" size="2x"/>
                                            <font-awesome-icon v-else-if="props.item.category === 'Video'"
                                                               :icon="['fal', 'file-video']" size="2x"/>
                                        </div>
                                    </td>
                                    <td>{{ props.item.name | longFileName}}</td>
                                    <td>
                                        {{ props.item.size}}
                                    </td>
                                    <td>
                                        <template v-if="props.item.category === 'Image'">
                                            {{ props.item.dimensions}}
                                        </template>
                                    </td>
                                    <td>{{ props.item.category}}
                                        <small v-if="props.item.mediaType">({{ props.item.mediaType}})</small>
                                    </td>
                                    <td>{{ props.item.author}}</td>
                                    <td>{{ props.item.updated | formatDate}}</td>
                                    <td v-bind:class="cssComputeStatusColor(props.item.status)">{{ props.item.status}}
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-layout>
            </v-container>
        </div>

    </v-content>
</template>

<script>
    import BaseLoadingComponent from './BaseLoading'

    import moment from '../services/moment'
    import lodash from '../services/lodash'
    import filesize from '../services/filesize'
    import helpers from '../services/helpers'

    import {
        SET_MEDIA_DASHBOARD_STATE_AND_RELOAD,
        SET_MEDIA_DASHBOARD_STATE,
        LOADING_PRISTINE,
        LOADING_STARTED,
        SET_MEDIA_DASHBOARD_DATA_TABLE_PAGINATION_STATE,
        GET_MEDIA_DASHBOARD_DATA_TABLE_PAGINATION_STATE,
        GET_FILTERED_MEDIA_ENTRIES,
    } from '../services/constants'

    export default {
        name: 'space-dashboard-media',
        extends: BaseLoadingComponent,
        data: function () {
            return {
                headers: [
                    {
                        text: '',
                        sortable: false,
                        value: 'url'
                    },
                    {text: 'Name', value: 'name'},
                    {text: 'Size', value: 'size', sortable: false},
                    {text: 'Dimensions', value: 'dimensions', sortable: false},
                    {text: 'Type', value: 'mediaType'},
                    {text: 'Author', value: 'author'},
                    {text: 'Updated', value: 'updated'},
                    {text: 'Status', value: 'status'},
                ],
            }
        },
        computed: {
            searchFilterTerm: {
                get() {
                    return this.$store.state.media.mediaDashboardState.searchFilterTerm
                },
                set(newValue) {
                    this.$store.commit(SET_MEDIA_DASHBOARD_STATE, {
                        searchFilterTerm: newValue
                    })
                }
            },
            mediaTypes: function () {
                const arr = this.$store.state.media.mediaTypes.filter((mediaType, index, self) => {
                    return self.findIndex(mediaType2 => {
                        return mediaType.category === mediaType2.category
                    }) === index
                })
                arr.unshift(this.allMediaType)
                return arr
            },
            assetsContentType() {
                return this.$store.state.media.assetsContentType
            },
            allMediaType() {
                return this.$store.state.media.allMediaType
            },
            selectedMediaFilter: function () {
                return this.$store.state.media.mediaDashboardState.selectedMediaType;
            },
            storePaginationSync: {
                get() {
                    return this.$store.getters[GET_MEDIA_DASHBOARD_DATA_TABLE_PAGINATION_STATE]
                },
                set(newValue) {
                    this.$store.commit(SET_MEDIA_DASHBOARD_DATA_TABLE_PAGINATION_STATE, newValue)
                }
            },
            mappedEntries: function () {
                return this.entries.map(entry => {
                    return {
                        id: entry.id,
                        name: this.getPrimaryDisplayName(entry),
                        size: this.getSizeOfEntry(entry),
                        dimensions: this.getDimensionsOfEntry(entry),
                        mediaType: this.getMediaTypeOfEntry(entry).displayName,
                        url: this.getUrlOfEntry(entry),
                        category: this.getMediaTypeOfEntry(entry).category,
                        contentTypeIdentifier: entry.contentType.identifier,
                        author: `${entry.author.firstName} ${entry.author.lastName}`,
                        updated: entry.updated,
                        status: entry.status.toUpperCase(),
                    }
                })
            },
            showArchived: {
                get() {
                    return this.$store.state.media.mediaDashboardState.showArchived
                },
                set(newValue) {
                    this.$store.dispatch(SET_MEDIA_DASHBOARD_STATE_AND_RELOAD, {
                        showArchived: newValue,
                    });
                }
            },
            entries() {
                return this.$store.getters[GET_FILTERED_MEDIA_ENTRIES]
            },
        },
        methods: {
            selectMediaFilter: function (mediaFilter) {
                this.$store.dispatch(SET_MEDIA_DASHBOARD_STATE_AND_RELOAD, {
                    selectedMediaType: mediaFilter,
                });
            },
            navigateEdit: function (mappedEntry) {
                this.$router.push(
                    {
                        name: 'spaceDashboardEditMedia',
                        params: {
                            id: mappedEntry.id,
                        }
                    })
            },
            getDimensionsOfEntry: function (entry) {
                const width = helpers.getFieldOfEntryByName(entry, 'width')
                const height = helpers.getFieldOfEntryByName(entry, 'height')
                if (height.includes('not found') || width.includes('not found')) {
                    return ''
                } else {
                    return `${width}x${height}`
                }
            },
            getSizeOfEntry: function (entry) {
                const size = helpers.getFieldOfEntryByName(entry, 'size')
                if (size.includes('not found')) {
                    return ''
                } else {
                    return filesize.filesize(helpers.getFieldOfEntryByName(entry, 'size'))
                }
            },
            getMediaTypeOfEntry(entry) {
                return helpers.getMediaTypeOfEntry(entry, this.$store.state)
            },
            getUrlOfEntry: function (entry) {
                return entry.fields.find(field => {
                    return field.identifier === 'url'
                }).value
            },
            getPrimaryDisplayName: helpers.getPrimaryDisplayName,
            cssComputeStatusColor: helpers.cssComputeStatusColor
        },
        filters: {
            formatDate: function (date) {
                return moment.formatFullDateAndTime(date);
            },
            longFileName: function (fileName) {
                const maxLength = 60
                // const endPeek = 15
                // return fileName.length > maxLength ? `${fileName.substring(0, maxLength - 3 - endPeek)}...${fileName.substring(fileName.length - endPeek, fileName.length)}` : fileName
                return fileName.length > maxLength ? `${fileName.substring(0, maxLength - 3)}...` : fileName
            },
        },
    }
</script>

<style scoped lang="scss">
    @import "../meta";

    .content-toolbar {
        margin-bottom: 4px;
        position: fixed;
        z-index: 4;
    }

    .content-container {
        display: flex;
        margin: 68px 0 0 $sidebar-width;
        height: calc(100% - 68px);
    }

    .dashboard-sidebar-container {
        height: 100%;
        position: fixed;
        left: 0;
    }

    .selected-filter {
        background: #F2F2F2;
    }

    .all-filter-divider {
        margin: 0 0 2em 0;
    }

    .interactive-data-table {
        cursor: pointer;
    }

    $size: 30px;
    .thumbnail-container {
        display: flex;
        justify-content: center;
        width: $size;
    }

    .data-table-thumbnail {
        max-height: $size;
        max-width: $size;
        vertical-align: middle;
    }

    .search-input-container {
        width: 500px;
        display: flex;
        align-items: center;
    }

    .search-input {
        margin: 0 1em 0 5em;
    }

    .add-asset-button-container {
        margin-right: 8px;
    }
</style>
